import type { WeaponAssetResolver } from '@sstk/weapon-kit';
import { CHASSIS_REGISTRY, COMPONENT_REGISTRY } from '@sstk/weapon-catalogue';

// Studio's own bundled copy of the Quaternius CC0 "Ultimate Guns Pack" GLBs
// (Public Domain), mirroring how the shared character rig GLB is duplicated
// per consumer rather than fetched from one shared location (see
// @sstk/weapon-kit's docs/loading.md). Referenced via Vite's fingerprinted-
// asset URL convention, matching the pattern already used elsewhere in this
// app (e.g. src/prototype/src/clips.ts for the character GLB).

const CHASSIS_URLS: Record<string, string> = {
  'assault-rifle-1': new URL('../assets/chassis/assault-rifle-1.glb', import.meta.url).href,
  'assault-rifle-2': new URL('../assets/chassis/assault-rifle-2.glb', import.meta.url).href,
  'assault-rifle-3': new URL('../assets/chassis/assault-rifle-3.glb', import.meta.url).href,
  'bullpup-1': new URL('../assets/chassis/bullpup-1.glb', import.meta.url).href,
  'pistol-1': new URL('../assets/chassis/pistol-1.glb', import.meta.url).href,
  'pistol-2': new URL('../assets/chassis/pistol-2.glb', import.meta.url).href,
  'pistol-3': new URL('../assets/chassis/pistol-3.glb', import.meta.url).href,
  'pistol-4': new URL('../assets/chassis/pistol-4.glb', import.meta.url).href,
  'revolver-1': new URL('../assets/chassis/revolver-1.glb', import.meta.url).href,
  'revolver-2': new URL('../assets/chassis/revolver-2.glb', import.meta.url).href,
  'revolver-3': new URL('../assets/chassis/revolver-3.glb', import.meta.url).href,
  'shotgun-1': new URL('../assets/chassis/shotgun-1.glb', import.meta.url).href,
  'shotgun-2': new URL('../assets/chassis/shotgun-2.glb', import.meta.url).href,
  'shotgun-sawed-off': new URL('../assets/chassis/shotgun-sawed-off.glb', import.meta.url).href,
  'shotgun-short-stock': new URL('../assets/chassis/shotgun-short-stock.glb', import.meta.url).href,
  'sniper-rifle-1': new URL('../assets/chassis/sniper-rifle-1.glb', import.meta.url).href,
  'sniper-rifle-2': new URL('../assets/chassis/sniper-rifle-2.glb', import.meta.url).href,
  'sniper-rifle-3': new URL('../assets/chassis/sniper-rifle-3.glb', import.meta.url).href,
  'sniper-rifle-4': new URL('../assets/chassis/sniper-rifle-4.glb', import.meta.url).href,
  'smg-1': new URL('../assets/chassis/smg-1.glb', import.meta.url).href,
  'smg-2': new URL('../assets/chassis/smg-2.glb', import.meta.url).href,
};

const COMPONENT_URLS: Record<string, string> = {
  bipod: new URL('../assets/attachments/bipod.glb', import.meta.url).href,
  bayonet: new URL('../assets/attachments/bayonet.glb', import.meta.url).href,
  scope: new URL('../assets/attachments/scope.glb', import.meta.url).href,
  tripod: new URL('../assets/attachments/tripod.glb', import.meta.url).href,
};

export const gunsmithAssetResolver: WeaponAssetResolver = {
  chassisUrl(chassisId) {
    const url = CHASSIS_URLS[chassisId];
    if (!url) throw new Error(`gunsmithAssetResolver: no bundled asset for chassisId "${chassisId}"`);
    return url;
  },
  componentUrl(componentId) {
    const url = COMPONENT_URLS[componentId];
    if (!url) throw new Error(`gunsmithAssetResolver: no bundled asset for componentId "${componentId}"`);
    return url;
  },
};

/** Every chassis a creator can pick from, in catalogue order. */
export function listChassisOptions(): { id: string; name: string; archetype: string }[] {
  return Object.values(CHASSIS_REGISTRY).map((c) => ({ id: c.id, name: c.name, archetype: c.archetype }));
}

/** Every component a creator can mount, in catalogue order. */
export function listComponentOptions(): { id: string; name: string; category: string }[] {
  return Object.values(COMPONENT_REGISTRY).map((c) => ({ id: c.id, name: c.name, category: c.category }));
}
